import { GraphQLVehicleGroupsRequester } from '@cobliteam/api-lib';
import { useQuery } from '@tanstack/react-query';

// PRD R1 (mirrors modules/groups/src/pages/GroupsPage.tsx's isGroupCountEligible): fleets with 2+
// groups are ineligible for the group AI suggestion fallback.
export const GROUP_COUNT_ELIGIBILITY_MAX = 1;
export const isGroupCountEligible = (groupCount: number): boolean => groupCount <= GROUP_COUNT_ELIGIBILITY_MAX;

const STALE_TIME_MS = 10 * 60 * 1000;

// Lazy instantiation (same pattern as useEntity/useVehicleGroup) so importing this module never
// touches @cobliteam/api-lib's constructor eagerly — only when the query actually runs.
const getVehicleGroupsRequester = () => new GraphQLVehicleGroupsRequester();

/**
 * AE-2569: cheap fleet-wide group-count check backing the "flag OR under-threshold" fallback for
 * group AI suggestions in the floating card (a fleet without `aiGroupRecommendations`/
 * `aiRecommendations2` should still see group suggestions while it has 0-1 groups).
 *
 * Only fetches when `enabled` — callers that already have flag-based access should pass `false` to
 * skip the extra request.
 */
const useIsGroupCountEligible = (fleetId: string | undefined, enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ['AI_RECOMMENDATIONS_GROUP_COUNT', fleetId],
    queryFn: () => getVehicleGroupsRequester().getVehicleGroups(),
    enabled: !!fleetId && enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { isEligible: isGroupCountEligible(data?.length ?? 0), isLoading };
};

export default useIsGroupCountEligible;
